import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Section from '../components/common/Section';
import { MiniESGForm } from '../components/MiniESGForm';
import { MiniESGScoreCard } from '../components/MiniESGScoreCard';
import { useMiniESGStore } from '../store/miniESGStore';
import { downloadMiniESGPDF } from '../utils/pdfGenerator';
import { useAIContext } from '../store/aiContextStore';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorBanner from '../components/common/ErrorBanner';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { importJSON } from '../utils/ImportJSON';
import { useToastStore } from '../store/toastStore';
import { useState as useReactState } from 'react';
import { ScoreHistory } from '../components/ScoreHistory';
import { AnalyticsSummaryPanel, type AnalyticsSummary } from '../components/AnalyticsSummaryPanel';
import { SECTOR_OPTIONS, REGION_OPTIONS, EMPLOYEE_BAND_OPTIONS } from '../constants/miniEsgMeta';
import { buildMiniEsgAuthHeaders, getMiniEsgApiBase } from '../utils/miniEsgApi';

async function hashMessage(text: string) {
  if (!text) return '';
  if (window.crypto?.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  return btoa(text).slice(0, 64);
}

export function MiniESG() {
  const { t } = useTranslation();
  const store = useMiniESGStore();
  const { setActivePanel } = useAIContext();
  const { showToast } = useToastStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [loadReportId, setLoadReportId] = useState('');
  const [loadPassword, setLoadPassword] = useState('');
  const [showReportList, setShowReportList] = useState(false);
  const [reportList, setReportList] = useState<Array<{
    id: string;
    companyName: string;
    taxId: string;
    score: any;
    createdAt: number;
    qrCodeUrl: string;
  }>>([]);
  const [reportSearchTerm, setReportSearchTerm] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [historyPoints, setHistoryPoints] = useState<Array<{ date: number; total: number }>>([]);
  const [analyticsSummary, setAnalyticsSummary] = useState<AnalyticsSummary | null>(null);
  const [analyticsFilters, setAnalyticsFilters] = useState<{ sector?: string; region?: string; employeeBand?: string }>({});
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<Error | null>(null);
  const [copilotMode, setCopilotMode] = useState<'form' | 'summary' | 'analyst'>('summary');
  const [copilotPrompt, setCopilotPrompt] = useState('');
  const [copilotResponse, setCopilotResponse] = useState<any>(null);
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [copilotError, setCopilotError] = useState<string | null>(null);
  const [copilotFeedback, setCopilotFeedback] = useState<'up' | 'down' | null>(null);
  const [copilotStreamText, setCopilotStreamText] = useState('');
  const [copilotStreaming, setCopilotStreaming] = useState(false);
  const requestCopilot = useMiniESGStore((state: any) => state.requestCopilot);

  React.useEffect(() => {
    setActivePanel('MiniESGPanel');
  }, [setActivePanel]);

  // Update AI context inputs when store changes
  React.useEffect(() => {
    const { setInput } = useAIContext.getState();
    setInput('miniESG_score_total', store.score.total);
    setInput('miniESG_score_environment', store.score.environment);
    setInput('miniESG_score_social', store.score.social);
    setInput('miniESG_score_governance', store.score.governance);
    setInput('miniESG_score_level', store.score.level);
    setInput('miniESG_companyName', store.companyName);
    setInput('miniESG_taxId', store.taxId);
    setInput('miniESG_recommendations', store.recommendations);
    setInput('miniESG_reportDate', store.reportDate);
    setInput('miniESG_savedReportId', store.savedReportId);
    setInput('miniESG_qrCodeUrl', store.qrCodeUrl);
  }, [store.score, store.companyName, store.taxId, store.recommendations, store.reportDate, store.savedReportId, store.qrCodeUrl]);

  const handleExportPDF = async () => {
    setError(null);
    try {
    await downloadMiniESGPDF();
      showToast(t("miniESG.exportPDFSuccess") || "PDF exported successfully", "success");
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t("miniESG.exportPDFFailed") || "Failed to export PDF");
      setError(err);
      showToast(err.message, "error");
    }
  };

  const handleExportJSON = () => {
    setError(null);
    try {
    const data = {
      companyName: store.companyName,
      taxId: store.taxId,
      answers: store.answers,
      score: store.score,
      recommendations: store.recommendations,
      reportDate: store.reportDate,
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mini-esg-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
      showToast(t("miniESG.exportJSONSuccess") || "JSON exported successfully", "success");
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t("miniESG.exportJSONFailed") || "Failed to export JSON");
      setError(err);
      showToast(err.message, "error");
    }
  };

  const handleImportJSON = async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await importJSON<{
        companyName?: string;
        taxId?: string;
        answers?: any;
        score?: any;
        recommendations?: string[];
        reportDate?: string;
      }>();
      
      if (data) {
        store.importReport(data);
        showToast(t("miniESG.importSuccess") || "Report imported successfully", "success");
      }
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t("miniESG.importFailed") || "Failed to import report");
      setError(err);
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReport = async () => {
    if (!store.companyName) {
      showToast(t("miniESG.companyNameRequired") || "Please enter company name", "warning");
      return;
    }
    
    setError(null);
    setLoading(true);
    try {
      const result = await store.saveReport();
      if (result) {
        showToast(
          `${t("miniESG.saveSuccess") || "Report saved successfully"} - ID: ${result.id}`,
          "success"
        );
      } else {
        throw new Error(t("miniESG.saveFailed") || "Failed to save report");
      }
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t("miniESG.saveFailed") || "Failed to save report");
      setError(err);
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLoadReport = async () => {
    if (!loadReportId.trim()) {
      showToast(t("miniESG.reportIdRequired") || "Please enter report ID", "warning");
      return;
    }
    
    setError(null);
    setLoading(true);
    try {
      const success = await store.loadReport(loadReportId.trim(), loadPassword || undefined);
      if (success) {
        showToast(t("miniESG.loadSuccess") || "Report loaded successfully", "success");
        setLoadReportId('');
        setLoadPassword('');
      } else {
        throw new Error(t("miniESG.loadFailed") || "Failed to load report");
      }
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t("miniESG.loadFailed") || "Failed to load report");
      setError(err);
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (confirm(t("miniESG.resetConfirm") || "Are you sure you want to delete all answers?")) {
      store.reset();
      showToast(t("miniESG.resetSuccess") || "Form reset successfully", "info");
    }
  };

  const handleLoadReportList = async () => {
    setError(null);
    setLoading(true);
    try {
      const reports = await store.listReports();
      setReportList(reports);
      setShowReportList(true);
      showToast(t("miniESG.reportListLoaded") || `${reports.length} reports loaded`, "success");
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t("miniESG.reportListFailed") || "Failed to load report list");
      setError(err);
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLoadReportFromList = async (reportId: string) => {
    setLoadReportId(reportId);
    setShowReportList(false);
    // Wait a bit for state update, then load
    setTimeout(async () => {
      await handleLoadReport();
    }, 100);
  };

  const handleUpdateReport = async () => {
    if (!store.savedReportId) {
      showToast(t("miniESG.noSavedReportToUpdate") || "No saved report to update. Please save first.", "warning");
      return;
    }
    
    if (!store.companyName) {
      showToast(t("miniESG.companyNameRequired") || "Please enter company name", "warning");
      return;
    }
    
    setError(null);
    setLoading(true);
    try {
      const success = await store.updateReport(store.savedReportId);
      if (success) {
        showToast(t("miniESG.updateSuccess") || "Report updated successfully", "success");
        // Reload report list if it's open
        if (showReportList) {
          await handleLoadReportList();
        }
      } else {
        throw new Error(t("miniESG.updateFailed") || "Failed to update report");
      }
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t("miniESG.updateFailed") || "Failed to update report");
      setError(err);
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    if (!confirm(t("miniESG.deleteConfirm") || "Are you sure you want to delete this report?")) {
      return;
    }
    
    setError(null);
    setLoading(true);
    try {
      const success = await store.deleteReport(reportId);
      if (success) {
        showToast(t("miniESG.deleteSuccess") || "Report deleted successfully", "success");
        // Remove from local list
        setReportList(reportList.filter(r => r.id !== reportId));
        // If it was the currently saved report, clear it
        if (store.savedReportId === reportId) {
          // Report ID is already cleared in store.deleteReport
        }
      } else {
        throw new Error(t("miniESG.deleteFailed") || "Failed to delete report");
      }
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t("miniESG.deleteFailed") || "Failed to delete report");
      setError(err);
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    setError(null);
    try {
      if (reportList.length === 0) {
        showToast(t("miniESG.noReportsToExport") || "No reports to export. Please load reports first.", "warning");
        return;
      }

      // CSV header
      const headers = ['ID', 'Company Name', 'Tax ID', 'Environment Score', 'Social Score', 'Governance Score', 'Total Score', 'Level', 'Created Date', 'QR Code URL'];
      const rows = reportList.map(report => [
        report.id,
        report.companyName || '',
        report.taxId || '',
        report.score.environment.toString(),
        report.score.social.toString(),
        report.score.governance.toString(),
        report.score.total.toString(),
        report.score.level,
        new Date(report.createdAt).toISOString(),
        report.qrCodeUrl || ''
      ]);

      // Escape CSV values
      const escapeCSV = (value: string) => {
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };

      const csvContent = [
        headers.map(escapeCSV).join(','),
        ...rows.map(row => row.map(escapeCSV).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mini-esg-reports-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast(t("miniESG.exportCSVSuccess") || "CSV exported successfully", "success");
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t("miniESG.exportCSVFailed") || "Failed to export CSV");
      setError(err);
      showToast(err.message, "error");
    }
  };

  // Filter reports by search term
  const filteredReports = React.useMemo(() => {
    if (!reportSearchTerm.trim()) {
      return reportList;
    }
    const search = reportSearchTerm.toLowerCase();
    return reportList.filter(report => 
      report.companyName?.toLowerCase().includes(search) ||
      report.taxId?.toLowerCase().includes(search) ||
      report.id.toLowerCase().includes(search)
    );
  }, [reportList, reportSearchTerm]);

  const buildAuthHeaders = React.useCallback(() => buildMiniEsgAuthHeaders(), []);
  const API_BASE = React.useMemo(() => getMiniEsgApiBase(), []);

  const handleLoadHistory = async () => {
    if (!store.companyName) {
      showToast(t('miniESG.historyCompanyRequired') || 'Enter company name to load history', 'warning');
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/analytics/history/${encodeURIComponent(store.companyName)}`, {
        headers: buildAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(t('miniESG.historyFailed') || 'Failed to load history');
      }
      const data = await response.json();
      const points = (data?.points || []).map((p: any) => ({ date: p.date, total: p.total }));
      setHistoryPoints(points);
      setShowHistory(true);
      showToast(t('miniESG.historyLoaded') || 'History loaded', 'success');
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t('miniESG.historyFailed') || 'Failed to load history');
      setError(err);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchAnalytics = async () => {
    try {
      setAnalyticsError(null);
      setAnalyticsLoading(true);
      const params = new URLSearchParams();
      if (analyticsFilters.sector) params.append('sector', analyticsFilters.sector);
      if (analyticsFilters.region) params.append('region', analyticsFilters.region);
      if (analyticsFilters.employeeBand) params.append('employeeBand', analyticsFilters.employeeBand);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_BASE}/analytics/summary${query}`, {
        headers: buildAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(t('miniESG.analyticsError') || 'Failed to load analytics');
      }
      const json = await response.json();
      setAnalyticsSummary(json);
      showToast(t('miniESG.analyticsLoaded') || 'Analytics updated', 'success');
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t('miniESG.analyticsError') || 'Failed to load analytics');
      setAnalyticsError(err);
      showToast(err.message, 'error');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleAskCopilot = async () => {
    if (!store.companyName) {
      showToast(t('miniESG.companyNameRequired') || 'Please enter company name', 'warning');
      return;
    }
    setCopilotError(null);
    setCopilotLoading(true);
    setCopilotFeedback(null);
    setCopilotStreamText('');
    const shouldStream = copilotMode !== 'form';
    if (shouldStream) setCopilotStreaming(true);
    let buffer = '';
    try {
      const response = await requestCopilot({
        mode: copilotMode,
        prompt: copilotPrompt.trim() || undefined,
        stream: shouldStream,
        onChunk: shouldStream
          ? (chunk) => {
              buffer += chunk;
              setCopilotStreamText(buffer);
            }
          : undefined,
      });
      const finalMessage = shouldStream ? (buffer || response?.message || '') : response?.message || '';
      const hash = await hashMessage(finalMessage);
      setCopilotResponse({ ...response, message: finalMessage, hash });
      showToast(t('miniESG.copilotSuccess') || 'Copilot ready', 'success');
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : t('miniESG.copilotError') || 'Copilot failed';
      setCopilotError(errMsg);
      showToast(errMsg, 'error');
    } finally {
      setCopilotLoading(false);
      setCopilotStreaming(false);
    }
  };

  const handleCopilotFeedback = (type: 'up' | 'down') => {
    if (!copilotResponse?.message) return;
    setCopilotFeedback(type);
    fetch(`${getMiniEsgApiBase()}/ai/copilot/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...buildMiniEsgAuthHeaders() },
      body: JSON.stringify({
        responseHash: copilotResponse?.hash || copilotResponse?.message?.slice(0, 64),
        feedback: type,
        reportId: store.savedReportId,
        mode: copilotMode,
      }),
    }).catch(() => {/* noop */});
    showToast(t('miniESG.copilotFeedbackThanks') || 'Thanks for the feedback!', 'info');
  };

  // Keyboard shortcuts
  useKeyboardShortcuts([
    { key: 'e', ctrl: true, action: handleExportJSON, description: 'Export JSON' },
    { key: 'i', ctrl: true, action: handleImportJSON, description: 'Import JSON' },
    { key: 's', ctrl: true, action: handleSaveReport, description: 'Save report' },
  ]);

  // Calculate completion percentage
  const totalQuestions = 15;
  const answeredQuestions = Object.values(store.answers).filter(v => v !== undefined && v !== null && v !== '' && v !== false).length;
  const completionPercentage = Math.round((answeredQuestions / totalQuestions) * 100);

  return (
    <div className="space-y-6">
        <Section title={t("miniESG.title") || "EVAT Mini ESG™ - SME Sustainability Assessment"}>
        <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />
        
        {loading && <LoadingSpinner />}

        {/* Welcome/Intro Section */}
        {!store.companyName && answeredQuestions === 0 && (
          <div className="mb-6 p-6 border border-evat-neon/30 rounded-lg bg-evat-neon/5">
            <h3 className="text-lg font-orbitron text-evat-neon mb-3">
              {t("miniESG.welcomeTitle") || "Welcome to EVAT Mini ESG™"}
            </h3>
            <p className="text-sm text-neutral-300 mb-4">
              {t("miniESG.welcomeDesc") || "A simplified 15-question sustainability assessment designed for small and medium enterprises (SMEs). Complete the form to receive your ESG score and personalized recommendations."}
            </p>
            <div className="space-y-2 text-xs text-neutral-400">
              <p><strong className="text-evat-neon">Environment (6 questions):</strong> {t("miniESG.introEnv") || "Energy consumption, waste management, eco-materials, and environmental goals."}</p>
              <p><strong className="text-blue-400">Social (5 questions):</strong> {t("miniESG.introSocial") || "Employee management, workplace safety, training, equality policies, and social projects."}</p>
              <p><strong className="text-purple-400">Governance (4 questions):</strong> {t("miniESG.introGov") || "Compliance, ethics, data security, and sustainability reporting."}</p>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        {answeredQuestions > 0 && (
          <div className="mb-6 p-4 border border-evat-gold/30 rounded-lg bg-evat-gold/5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-neutral-300">
                {t("miniESG.progress") || "Form Progress"}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-neutral-500 italic">
                  {t("miniESG.autoSaveEnabled") || "Auto-save enabled"}
                </span>
                <span className="text-sm font-orbitron text-evat-gold">
                  {completionPercentage}%
                </span>
              </div>
            </div>
            <div className="w-full h-2 bg-black rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-evat-neon to-evat-gold transition-all duration-300"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
            <div className="mt-2 text-xs text-neutral-400">
              {answeredQuestions} / {totalQuestions} {t("miniESG.questionsAnswered") || "questions answered"}
            </div>
          </div>
        )}
        
        <div className="mb-6 space-y-4">
          {/* Auth Section */}
          <AuthSection />
          
          {/* Save/Load Section */}
          <div className="flex flex-wrap gap-3 items-end p-4 border border-evat-neon/20 rounded-lg bg-[#0b0b0b]">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm text-neutral-400 mb-2">
                {t("miniESG.loadReportId") || "Load Report ID"}
              </label>
              <input
                type="text"
                value={loadReportId}
                onChange={(e) => setLoadReportId(e.target.value)}
                placeholder={t("miniESG.reportIdPlaceholder") || "Enter report ID..."}
                className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
              />
            </div>
            <div className="min-w-[200px]">
              <label className="block text-sm text-neutral-400 mb-2">
                {t('miniESG.loadPassword') || 'Password (for private reports)'}
              </label>
              <input
                type="password"
                value={loadPassword}
                onChange={(e) => setLoadPassword(e.target.value)}
                placeholder={t('miniESG.passwordPlaceholder') || 'Enter password if required'}
                className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
              />
            </div>
            <button
              onClick={handleLoadReport}
              disabled={loading}
              className="px-4 py-2 bg-blue-900/20 border border-blue-500 rounded hover:bg-blue-900/30 transition-colors font-orbitron text-sm text-blue-400 disabled:opacity-50"
            >
              📥 {t("miniESG.loadReport") || "Load Report"}
            </button>
            <button
              onClick={handleSaveReport}
              disabled={loading || !store.companyName}
              className="px-4 py-2 bg-green-900/20 border border-green-500 rounded hover:bg-green-900/30 transition-colors font-orbitron text-sm text-green-400 disabled:opacity-50"
            >
              💾 {t("miniESG.saveReport") || "Save Report"}
            </button>
            {store.savedReportId && (
              <button
                onClick={handleUpdateReport}
                disabled={loading || !store.companyName}
                className="px-4 py-2 bg-orange-900/20 border border-orange-500 rounded hover:bg-orange-900/30 transition-colors font-orbitron text-sm text-orange-400 disabled:opacity-50"
                title={t("miniESG.updateReport") || "Update saved report"}
              >
                🔄 {t("miniESG.updateReport") || "Update Report"}
              </button>
            )}
          </div>
          
          {/* Saved Report Info */}
          {store.savedReportId && (
            <div className="p-4 border border-green-500/30 rounded-lg bg-green-900/10">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-green-400 mb-1">
                    {t("miniESG.savedReportInfo") || "Saved Report ID"}
                  </div>
                  <div className="text-xs text-neutral-300 font-mono break-all">
                    {store.savedReportId}
                  </div>
                  {store.qrCodeUrl && (
                    <div className="text-xs text-neutral-400 mt-1 break-all">
                      QR: {store.qrCodeUrl}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (store.savedReportId) {
                        navigator.clipboard.writeText(store.savedReportId);
                        showToast(t("miniESG.copyId") || "Copy ID", "info");
                      }
                    }}
                    className="px-3 py-1 text-xs bg-blue-900/20 border border-blue-500/50 rounded hover:bg-blue-900/30 text-blue-400"
                  >
                    {t("miniESG.copyId") || "Copy ID"}
                  </button>
                  <button
                    onClick={() => {
                      const base = process.env.MINI_ESG_BASE_URL || "https://evat.esg";
                      const link = store.qrCodeUrl || (store.savedReportId ? `${base}/report/${store.savedReportId}` : "");
                      if (link) {
                        navigator.clipboard.writeText(link);
                        showToast(t("miniESG.copyLinkSuccess") || "Link copied", "success");
                      } else {
                        showToast(t("miniESG.copyLinkFailed") || "No link available", "warning");
                      }
                    }}
                    className="px-3 py-1 text-xs bg-teal-900/20 border border-teal-500/50 rounded hover:bg-teal-900/30 text-teal-400"
                    title={t("miniESG.copyLink") || "Copy Link"}
                  >
                    🔗 {t("miniESG.copyLink") || "Copy Link"}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Export/Import/Reset Section */}
          <div className="flex flex-wrap gap-3">
            {/* Visibility & password for saving */}
            <div className="flex items-end gap-3 flex-wrap">
              <div className="min-w-[180px]">
                <label className="block text-sm text-neutral-400 mb-2">{t('miniESG.visibility') || 'Visibility'}</label>
                <select
                  value={store.visibility || 'public'}
                  onChange={(e) => useMiniESGStore.setState({ visibility: e.target.value as any })}
                  className="w-full px-3 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none text-sm"
                >
                  <option value="public">{t('miniESG.public') || 'Public'}</option>
                  <option value="private">{t('miniESG.private') || 'Private'}</option>
                </select>
              </div>
              <div className="min-w-[220px]">
                <label className="block text-sm text-neutral-400 mb-2">{t('miniESG.password') || 'Password'}</label>
                <input
                  type="password"
                  value={store.reportPassword || ''}
                  onChange={(e) => useMiniESGStore.setState({ reportPassword: e.target.value })}
                  placeholder={t('miniESG.passwordPlaceholder') || 'Set a password for private reports'}
                  className="w-full px-3 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
                />
                {store.visibility === 'private' && (
                  <div className="mt-1 text-xs text-neutral-500">{t('miniESG.privateNote') || 'Private reports require the password to load.'}</div>
                )}
              </div>
            </div>
          <button
            onClick={handleExportPDF}
              disabled={!store.companyName || loading}
              className="px-6 py-2 bg-evat-neon/20 border border-evat-neon rounded hover:bg-evat-neon/30 transition-colors font-orbitron text-sm disabled:opacity-50"
          >
            📄 {t("miniESG.exportPDF") || "PDF Export"}
          </button>
          <button
            onClick={handleExportJSON}
              disabled={!store.companyName || loading}
              className="px-6 py-2 bg-evat-gold/20 border border-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron text-sm disabled:opacity-50"
          >
              💾 {t("miniESG.exportJSON") || "JSON Export"} (Ctrl+E)
            </button>
            <button
              onClick={handleImportJSON}
              disabled={loading}
              className="px-6 py-2 bg-purple-900/20 border border-purple-500 rounded hover:bg-purple-900/30 transition-colors font-orbitron text-sm text-purple-400 disabled:opacity-50"
            >
              📤 {t("miniESG.importJSON") || "Import JSON"} (Ctrl+I)
          </button>
          <button
            onClick={handleReset}
              disabled={loading}
              className="px-6 py-2 bg-red-900/20 border border-red-500 rounded hover:bg-red-900/30 transition-colors font-orbitron text-sm text-red-400 disabled:opacity-50"
          >
            🔄 {t("miniESG.reset") || "Reset"}
          </button>
            <button
              onClick={handleLoadReportList}
              disabled={loading}
              className="px-6 py-2 bg-cyan-900/20 border border-cyan-500 rounded hover:bg-cyan-900/30 transition-colors font-orbitron text-sm text-cyan-400 disabled:opacity-50"
            >
              📋 {t("miniESG.listReports") || "List Reports"}
            </button>
            <button
              onClick={handleLoadHistory}
              disabled={loading}
              className="px-6 py-2 bg-violet-900/20 border border-violet-500 rounded hover:bg-violet-900/30 transition-colors font-orbitron text-sm text-violet-300 disabled:opacity-50"
            >
              📈 {t('miniESG.loadHistory') || 'Load History'}
            </button>
            {reportList.length > 0 && (
              <button
                onClick={handleExportCSV}
                disabled={loading}
                className="px-6 py-2 bg-teal-900/20 border border-teal-500 rounded hover:bg-teal-900/30 transition-colors font-orbitron text-sm text-teal-400 disabled:opacity-50"
              >
                📊 {t("miniESG.exportCSV") || "Export CSV"}
              </button>
            )}
          </div>
        </div>

        {/* Report List */}
        {showReportList && reportList.length > 0 && (
          <div className="mt-6 border border-cyan-500/30 rounded-lg p-4 bg-[#0b0b0b]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-orbitron text-cyan-400">
                {t("miniESG.savedReports") || "Saved Reports"} ({filteredReports.length} / {reportList.length})
              </h3>
              <button
                onClick={() => setShowReportList(false)}
                className="text-xs text-neutral-400 hover:text-neutral-200"
              >
                ✕ {t("miniESG.close") || "Close"}
              </button>
            </div>
            
            {/* Search/Filter */}
            <div className="mb-4">
              <input
                type="text"
                value={reportSearchTerm}
                onChange={(e) => setReportSearchTerm(e.target.value)}
                placeholder={t("miniESG.searchReports") || "Search by company name, tax ID, or report ID..."}
                className="w-full px-4 py-2 bg-black border border-cyan-500/30 rounded text-neutral-100 focus:border-cyan-500 focus:outline-none"
              />
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredReports.length === 0 ? (
                <div className="text-center text-neutral-400 py-4">
                  {t("miniESG.noMatchingReports") || "No matching reports found"}
                </div>
              ) : (
                filteredReports.map((report) => (
                <div
                  key={report.id}
                  className="flex items-center justify-between p-3 border border-cyan-500/20 rounded bg-black/50 hover:bg-black/70 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-neutral-100 font-medium truncate">
                      {report.companyName || t("miniESG.unnamedCompany") || "Unnamed Company"}
                    </div>
                    <div className="text-xs text-neutral-400 mt-1">
                      {report.taxId} • {new Date(report.createdAt).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-neutral-500 mt-1 font-mono truncate">
                      ID: {report.id}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        report.score.level === 'red' ? 'bg-red-900/30 text-red-400' :
                        report.score.level === 'yellow' ? 'bg-yellow-900/30 text-yellow-400' :
                        'bg-green-900/30 text-green-400'
                      }`}>
                        {report.score.total} / 30
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleLoadReportFromList(report.id)}
                      disabled={loading}
                      className="px-3 py-1 text-xs bg-blue-900/20 border border-blue-500/50 rounded hover:bg-blue-900/30 text-blue-400 whitespace-nowrap disabled:opacity-50"
                    >
                      {t("miniESG.load") || "Load"}
                    </button>
                    <button
                      onClick={() => handleDeleteReport(report.id)}
                      disabled={loading}
                      className="px-3 py-1 text-xs bg-red-900/20 border border-red-500/50 rounded hover:bg-red-900/30 text-red-400 whitespace-nowrap disabled:opacity-50"
                      title={t("miniESG.deleteReport") || "Delete report"}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))
              )}
            </div>
          </div>
        )}

        {showReportList && reportList.length === 0 && (
          <div className="mt-6 border border-cyan-500/30 rounded-lg p-4 bg-[#0b0b0b] text-center text-neutral-400">
            {t("miniESG.noReports") || "No saved reports found"}
          </div>
        )}

        <MiniESGForm />
        {/* Score History */}
        {showHistory && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-orbitron text-violet-300">{t('miniESG.scoreHistory') || 'Score History'}</h3>
              <button onClick={() => setShowHistory(false)} className="text-xs text-neutral-400 hover:text-neutral-200">
                ✕ {t('miniESG.close') || 'Close'}
              </button>
            </div>
            <ScoreHistory points={historyPoints} />
          </div>
        )}

        <div className="mt-6 border border-evat-neon/20 rounded-lg p-4 bg-[#0b0b0b]">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-lg font-orbitron text-evat-neon">{t('miniESG.analyticsTitle') || 'Premium Analytics (beta)'}</h3>
              <p className="text-sm text-neutral-400">{t('miniESG.analyticsDescription') || 'Multi-report KPIs, distribution, and benchmarks across your portfolio.'}</p>
            </div>
            <button
              onClick={handleFetchAnalytics}
              disabled={analyticsLoading}
              className="self-start md:self-auto px-4 py-2 bg-evat-neon/10 border border-evat-neon rounded text-evat-neon font-orbitron text-sm disabled:opacity-50"
            >
              {analyticsLoading ? '…' : (t('miniESG.analyticsRefresh') || 'Refresh')}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">{t('miniESG.sectorLabel') || 'Sector'}</label>
              <select
                value={analyticsFilters.sector || ''}
                onChange={(e) => setAnalyticsFilters((prev) => ({ ...prev, sector: e.target.value || undefined }))}
                className="w-full px-3 py-2 bg-black border border-evat-neon/20 rounded text-sm text-neutral-100"
              >
                {SECTOR_OPTIONS.map((option) => (
                  <option key={option.value || 'none'} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">{t('miniESG.regionLabel') || 'Region'}</label>
              <select
                value={analyticsFilters.region || ''}
                onChange={(e) => setAnalyticsFilters((prev) => ({ ...prev, region: e.target.value || undefined }))}
                className="w-full px-3 py-2 bg-black border border-evat-neon/20 rounded text-sm text-neutral-100"
              >
                {REGION_OPTIONS.map((option) => (
                  <option key={option.value || 'none'} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">{t('miniESG.employeeBandLabel') || 'Employee band'}</label>
              <select
                value={analyticsFilters.employeeBand || ''}
                onChange={(e) => setAnalyticsFilters((prev) => ({ ...prev, employeeBand: e.target.value || undefined }))}
                className="w-full px-3 py-2 bg-black border border-evat-neon/20 rounded text-sm text-neutral-100"
              >
                {EMPLOYEE_BAND_OPTIONS.map((option) => (
                  <option key={option.value || 'none'} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          {analyticsError && (
            <div className="mt-4 text-sm text-red-400">
              {analyticsError.message}
            </div>
          )}
          <div className="mt-4">
            {analyticsSummary ? (
              <AnalyticsSummaryPanel summary={analyticsSummary} />
            ) : (
              <div className="text-sm text-neutral-500 border border-neutral-800 rounded p-3">
                {t('miniESG.analyticsEmpty') || 'Load analytics to see KPIs once multiple reports are saved.'}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 border border-blue-400/30 rounded-lg p-4 bg-[#07090d] space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-lg font-orbitron text-blue-300">{t('miniESG.copilotTitle') || 'Mini ESG Copilot'}</h3>
              <p className="text-sm text-neutral-400">{t('miniESG.copilotDescription') || 'Get tailored hints and summaries based on your answers. Responses stay private and respect your visibility settings.'}</p>
            </div>
            <div className="flex gap-2">
              <select
                value={copilotMode}
                onChange={(e) => setCopilotMode(e.target.value as typeof copilotMode)}
                className="px-3 py-2 bg-black border border-blue-500/40 rounded text-sm text-neutral-100"
              >
                <option value="form">{t('miniESG.copilotModeForm') || 'Form hints'}</option>
                <option value="summary">{t('miniESG.copilotModeSummary') || 'Executive summary'}</option>
                <option value="analyst">{t('miniESG.copilotModeAnalyst') || 'Analyst insights'}</option>
              </select>
              <button
                onClick={handleAskCopilot}
                disabled={copilotLoading}
                className="px-4 py-2 bg-blue-500/20 border border-blue-400 rounded text-blue-200 font-orbitron text-sm disabled:opacity-50"
              >
                {copilotLoading ? '…' : (t('miniESG.copilotAsk') || 'Ask Copilot')}
              </button>
            </div>
          </div>
          <textarea
            value={copilotPrompt}
            onChange={(e) => setCopilotPrompt(e.target.value)}
            placeholder={t('miniESG.copilotPromptPlaceholder') || 'Optional context or question...'}
            className="w-full min-h-[70px] px-4 py-2 bg-black border border-blue-500/30 rounded text-neutral-100 focus:border-blue-400 focus:outline-none text-sm"
          />
          {copilotError && (
            <div className="text-sm text-red-400">{copilotError}</div>
          )}
          {copilotStreaming && (
            <div className="border border-blue-500/30 rounded-lg p-4 bg-black/40 space-y-2">
              <div className="text-xs uppercase tracking-wide text-neutral-400">{t('miniESG.copilotStreaming') || 'Streaming response...'}</div>
              <p className="text-sm text-neutral-100 whitespace-pre-line">{copilotStreamText || '...'}</p>
            </div>
          )}
          {copilotResponse && !copilotStreaming ? (
            <div className="border border-blue-500/30 rounded-lg p-4 bg-black/40 space-y-3">
              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>{t('miniESG.copilotProvider') || 'Provider'}: {copilotResponse.provider}</span>
                {copilotResponse.fallback && <span className="text-amber-400">{t('miniESG.copilotFallback') || 'Fallback mode'}</span>}
              </div>
              <p className="text-sm text-neutral-100 whitespace-pre-line">{copilotResponse.message}</p>
              {Array.isArray(copilotResponse.suggestions) && copilotResponse.suggestions.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-neutral-400 mb-1">{t('miniESG.copilotSuggestions') || 'Suggestions'}</div>
                  <ul className="list-disc list-inside text-sm text-neutral-200 space-y-1">
                    {copilotResponse.suggestions.map((s: string, idx: number) => (
                      <li key={idx}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex items-center gap-3 text-xs text-neutral-400">
                <span>{t('miniESG.copilotFeedbackPrompt') || 'Was this helpful?'}</span>
                <button
                  onClick={() => handleCopilotFeedback('up')}
                  className={`px-2 py-1 rounded border ${copilotFeedback === 'up' ? 'border-green-400 text-green-300' : 'border-neutral-600'}`}
                >
                  👍
                </button>
                <button
                  onClick={() => handleCopilotFeedback('down')}
                  className={`px-2 py-1 rounded border ${copilotFeedback === 'down' ? 'border-red-400 text-red-300' : 'border-neutral-600'}`}
                >
                  👎
                </button>
              </div>
            </div>
          ) : (
            !copilotStreaming && (
              <div className="text-sm text-neutral-500 border border-dashed border-blue-500/30 rounded p-3">
                {t('miniESG.copilotEmpty') || 'Ask the copilot to see contextual guidance and remediation steps.'}
              </div>
            )
          )}
        </div>
        
        {(store.score.total > 0 || Object.keys(store.answers).length > 0) && (
          <div className="mt-8">
            <MiniESGScoreCard />
          </div>
        )}
      </Section>
    </div>
  );
}

function AuthSection() {
  const { t } = useTranslation();
  const { showToast } = useToastStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const login = useMiniESGStore((s: any) => (s as any).login);
  
  const onLogin = async () => {
    setBusy(true);
    try {
      await login(email, password);
      showToast(t('login.success') || 'Logged in', 'success');
      setPassword('');
    } catch (e) {
      showToast(t('login.failed') || 'Login failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-3 items-end p-4 border border-evat-neon/20 rounded-lg bg-[#0b0b0b]">
      <div className="min-w-[220px]">
        <label className="block text-sm text-neutral-400 mb-2">{t('login.email') || 'Email'}</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@evatlabs.org"
          className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
        />
      </div>
      <div className="min-w-[200px]">
        <label className="block text-sm text-neutral-400 mb-2">{t('login.password') || 'Password'}</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="w-full px-4 py-2 bg-black border border-evat-neon/30 rounded text-neutral-100 focus:border-evat-neon focus:outline-none"
        />
      </div>
      <button
        onClick={onLogin}
        disabled={busy}
        className="px-4 py-2 bg-evat-neon/20 border border-evat-neon rounded hover:bg-evat-neon/30 transition-colors font-orbitron text-sm text-evat-neon disabled:opacity-50"
      >
        {busy ? '…' : (t('login.login') || 'Login')}
      </button>
    </div>
  );
}

